import { DefaultTFuncReturn } from 'i18next';
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Notification } from '../../api/model';
import { usePostDeleteNotifications } from '../../api/notifications/notifications';
import { useDefaultOptions } from '../../hooks/api';
import * as sessionStorageUtils from '../../utils/sessionStorage';
import { CheckBox } from '../atoms/CheckBox';
import Link from '../atoms/Link';
import { FieldValues } from '../pages/privileges/edit/PrivilegesEditPage';
import ConfirmModal from './ConfirmModal';
import ErrorModal from './ErrorModal';
import Modal from './Modal';
import SpinnerModal from './SpinnerModal';

// 一覧のソートキー
type SortKey = 'created_at' | 'production_no' | 'title' | 'request_user_id_text' | 'message';

interface SortableThProps {
  label: DefaultTFuncReturn;
  onClick: () => void;
}

function SortableTh({ label, onClick }: SortableThProps) {
  return (
    <th>
      <button type="button" className="text-button" onClick={onClick}>
        {label}
        <i className="fa-solid fa-sort" />
      </button>
    </th>
  );
}

interface Props {
  active: boolean;
  notifications: Notification[];
  onClose: () => void;
  refech: () => Promise<void>;
}

/**
 * 通知モーダル
 * @param
 * @returns
 */
function NotificationsModal({ active, notifications, onClose, refech }: Props) {
  // 言語情報
  const {
    t,
    i18n: { language },
  } = useTranslation();

  // フォーム関連
  const { register, handleSubmit, reset } = useForm<{ [key: string]: boolean }>();

  // フォーム送信中
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ソート情報
  const [sortInfo, setSortInfo] = useState(
    sessionStorageUtils.get('notifications.SortInfo', { key: 'created_at' as SortKey, isDisc: false })
  );

  // 一覧データをソートしたもの
  const sortedDatas = useMemo(
    () =>
      [...(notifications ?? [])].sort((a, b) => {
        const aAny = a as { [key: string]: string | undefined };
        const bAny = b as { [key: string]: string | undefined };
        const multiValue = sortInfo.isDisc ? -1 : 1;
        if (aAny[sortInfo.key] !== bAny[sortInfo.key]) {
          // 第一ソートキー
          return ((aAny[sortInfo.key] ?? '') > (bAny[sortInfo.key] ?? '') ? 1 : -1) * multiValue;
        }
        return 1;
      }),
    [sortInfo, notifications]
  );

  // 一覧のソートキーを変更する関数
  const changeSortKey = useCallback((key: SortKey) => {
    setSortInfo((pre) => {
      const next = { key, isDisc: pre.key === key && !pre.isDisc };
      sessionStorageUtils.set('notifications.SortInfo', next);
      return next;
    });
  }, []);

  // 削除 API
  const options = useDefaultOptions(language);
  const { error: deleteError, mutateAsync: deleteAsync } = usePostDeleteNotifications(options);

  // 削除エラーの表示
  const [displayDeleteError, setDisplayDeleteError] = useState(false);

  useEffect(() => {
    setDisplayDeleteError(deleteError != null);
  }, [deleteError]);

  // 削除確認メッセージ
  const [displayConfirmDeletingModal, setDisplayConfirmDeletingModal] = useState(false);

  // 削除ボタン押下時
  const onClickDelete = useCallback(async () => {
    await handleSubmit((data) => {
      // 削除対象の ID を取得
      const deleteIds = sortedDatas.flatMap((x) => x.notification_id ?? []).filter((x) => data[x.toString()]);

      // 削除対象が 0 件の場合何もしない
      if (deleteIds.length === 0) {
        setIsSubmitting(false);
        return;
      }

      // 確認モーダルを表示
      setDisplayConfirmDeletingModal(true);
    })();
  }, [handleSubmit, sortedDatas]);

  // 削除実行
  const onDelete = useCallback(async () => {
    // 確認ダイアログを非表示
    setDisplayConfirmDeletingModal(false);

    // スピナーモーダルを表示
    setIsSubmitting(true);

    // フォームの送信の実処理
    await handleSubmit(async (data) => {
      // 成功時
      const onSuccess = async () => {
        try {
          await refech();
        } catch {
          // Do nothing
        } finally {
          setIsSubmitting(false);
        }
      };

      // 失敗時
      const onError = () => setIsSubmitting(false);

      // 削除対象の ID を取得
      const deleteIds = sortedDatas.flatMap((x) => x.notification_id ?? []).filter((x) => data[x.toString()]);

      // 削除対象が 0 件の場合何もしない
      if (deleteIds.length === 0) {
        setIsSubmitting(false);
        return;
      }

      // 削除実行
      await deleteAsync({ data: { notification_ids: deleteIds } }, { onSuccess, onError });
    })();
  }, [handleSubmit, sortedDatas, deleteAsync, refech]);

  return (
    <>
      <Modal active={active} additionalClassName="popupfr800" displayCloseButton onClose={onClose}>
        <form>
          {/* 削除ボタン */}
          <button type="button" className="deletebtn1 text-button" onClick={onClickDelete}>
            <i className="fa-solid fa-circle-xmark" />
            {t('削除')}
          </button>
          {/* 通知一覧 */}
          <table className="table3">
            <tbody>
              <tr>
                <th>
                  {/* 全選択チェックボックス */}
                  <span className="ck-box">
                    <input
                      type="checkbox"
                      id="all"
                      tabIndex={-1}
                      disabled={isSubmitting}
                      onClick={(e) => {
                        const { checked } = e.target as HTMLInputElement;

                        // 全選択／全解除
                        const next: FieldValues = {};
                        sortedDatas.forEach((x) => {
                          next[x.notification_id?.toString() ?? ''] = checked;
                        });
                        reset(next);
                      }}
                    />
                    <label
                      htmlFor="all"
                      // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
                      tabIndex={isSubmitting ? -1 : 0}
                      onKeyDown={(e) => {
                        if (e.code === 'Space') {
                          // TODO: document.getElementById を使わないようにする
                          document.getElementById('all')?.click();
                          e.preventDefault();
                        }
                      }}
                    >
                      &nbsp;
                    </label>
                  </span>
                </th>
                <SortableTh label={t('日時')} onClick={() => changeSortKey('created_at')} />
                <SortableTh label={t('資料管理No')} onClick={() => changeSortKey('production_no')} />
                <SortableTh label={t('タイトル')} onClick={() => changeSortKey('title')} />
                <SortableTh label={t('依頼者')} onClick={() => changeSortKey('request_user_id_text')} />
                <SortableTh label={t('内容')} onClick={() => changeSortKey('message')} />
              </tr>
              {sortedDatas.map((x) => {
                let link: ReactNode;
                // 通知の種類に応じて、トス資料タブ、確認依頼画面、承認申請画面に遷移（別タブ表示）させる
                if (x.toss_material_id != null) {
                  link = (
                    <Link to={`/${language}/productions/edit/${x.production_id ?? ''}#toss`} target="_blank">
                      {x.production_no}
                    </Link>
                  );
                } else if (x.confirmation_request_id != null) {
                  link = (
                    <Link
                      to={`/${language}/productions/edit/${x.production_id ?? ''}/confirmation-request/edit/${
                        x.confirmation_request_id
                      }`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: 'blue' }}
                    >
                      {x.production_no}
                    </Link>
                  );
                } else if (x.approval_application_id != null) {
                  link = (
                    <Link
                      to={`/${language}/productions/edit/${x.production_id ?? ''}/approval-application/edit/${
                        x.approval_application_id
                      }`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {x.production_no}
                    </Link>
                  );
                }
                return (
                  <tr key={x.notification_id}>
                    <td>
                      <CheckBox
                        label={undefined}
                        name={x.notification_id?.toString() ?? ''}
                        isDisabled={isSubmitting}
                        value={undefined}
                        register={register}
                      />
                    </td>
                    <td>{x.created_at?.replaceAll('-', '/')}</td>
                    <td>{link}</td>
                    <td>{x.title}</td>
                    <td>{x.request_user_id_text}</td>
                    <td>{x.message}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </form>
      </Modal>
      {/* 削除確認メッセージ */}
      {displayConfirmDeletingModal && (
        <ConfirmModal onClickOk={onDelete} onClickCancel={() => setDisplayConfirmDeletingModal(false)}>
          <div>{t('選択した項目を削除しますか？')}</div>
        </ConfirmModal>
      )}
      {/* フォーム送信中はスピナーモーダルを表示 */}
      {isSubmitting && <SpinnerModal />}
      {/* 削除エラーの表示 */}
      {displayDeleteError && <ErrorModal error={deleteError} onClickOk={() => setDisplayDeleteError(false)} />}
    </>
  );
}

export default NotificationsModal;
